import config from '../config.js';

/**
 * Storage utilities
 */
export const storage = {
  /**
   * Get session ID from localStorage
   * @returns {string|null}
   */
  getSessionId() {
    return localStorage.getItem(config.storage.sessionIdKey);
  },

  /**
   * Set session ID in localStorage
   * @param {string} sessionId
   */
  setSessionId(sessionId) {
    localStorage.setItem(config.storage.sessionIdKey, sessionId);
  },

  /**
   * Get user phone from localStorage
   * @returns {string|null}
   */
  getUserPhone() {
    return localStorage.getItem(config.storage.userPhoneKey);
  },

  /**
   * Set user phone in localStorage
   * @param {string} phone
   */
  setUserPhone(phone) {
    localStorage.setItem(config.storage.userPhoneKey, phone);
  },

  /**
   * Get user email from localStorage
   * @returns {string|null}
   */
  getUserEmail() {
    return localStorage.getItem(config.storage.userEmailKey);
  },

  /**
   * Set user email in localStorage
   * @param {string} email
   */
  setUserEmail(email) {
    localStorage.setItem(config.storage.userEmailKey, email);
  },

  /**
   * Get user profile from localStorage
   * @returns {Object|null}
   */
  getUserProfile() {
    const profile = localStorage.getItem(config.storage.userProfileKey);
    return profile ? JSON.parse(profile) : null;
  },

  /**
   * Set user profile in localStorage
   * @param {Object} profile - { nationality, employment_type, salary_range }
   */
  setUserProfile(profile) {
    localStorage.setItem(config.storage.userProfileKey, JSON.stringify(profile));
  },

  /**
   * Clear all stored data
   */
  clear() {
    localStorage.removeItem(config.storage.sessionIdKey);
    localStorage.removeItem(config.storage.userPhoneKey);
    localStorage.removeItem(config.storage.userEmailKey);
    localStorage.removeItem(config.storage.userProfileKey);
  },

  /**
   * Clear only session data (keep phone, email, and profile)
   */
  clearSession() {
    localStorage.removeItem(config.storage.sessionIdKey);
  },

  /**
   * Clear only Ollie-specific keys from localStorage
   * This is useful for error recovery without affecting other apps' data
   */
  clearOllieData() {
    const ollieKeys = [
      config.storage.sessionIdKey,
      config.storage.userPhoneKey,
      config.storage.userEmailKey,
      config.storage.userProfileKey,
    ];
    
    ollieKeys.forEach(key => {
      localStorage.removeItem(key);
    });
  },
};

/**
 * Set a cookie for bank attribution tracking
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Expiration in days
 */
export const setCookie = (name, value, days = 7) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

/**
 * Generate a bank redirect URL with Ollie session tracking
 * @param {string} bankUrl - The bank's credit card application URL
 * @param {string} sessionId - Current session ID for tracking
 * @param {string} cardName - Optional card name for tracking
 * @returns {string} - URL with tracking parameters
 */
export const generateBankUrl = (bankUrl, sessionId, cardName = null) => {
  if (!bankUrl || !sessionId) return bankUrl;
  
  // Set tracking cookie for bank to read
  setCookie('ol_session', sessionId, 60); // 60 days
  
  try {
    const url = new URL(bankUrl);
    url.searchParams.set('ol_session', sessionId);
    url.searchParams.set('source', 'ollie');
    if (cardName) {
      url.searchParams.set('card', cardName);
    }
    return url.toString();
  } catch (error) {
    // If URL parsing fails, append as query string
    const separator = bankUrl.includes('?') ? '&' : '?';
    let params = `ol_session=${encodeURIComponent(sessionId)}&source=ollie`;
    if (cardName) {
      params += `&card=${encodeURIComponent(cardName)}`;
    }
    return `${bankUrl}${separator}${params}`;
  }
};

/**
 * Sort questions and their options by sort_order
 * @param {import('../types.js').Question[]} questions - Array of questions
 * @returns {import('../types.js').Question[]} Sorted questions with sorted options
 */
export const sortQuestionsAndOptions = questions => {
  if (!Array.isArray(questions)) return [];

  return questions
    .map(question => ({
      ...question,
      options: [...question.options].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    }))
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
};

/**
 * Get emoji for question option
 * @param {string} questionKey - Question key
 * @param {string} optionKey - Option key
 * @returns {string}
 */
export const getEmojiForOption = (questionKey, optionKey) => {
  const emojiMap = {
    nationality_visa: {
      uae_national: '🇦🇪',
      gcc_national: '🏛️',
      expat_resident: '💼',
    },
    monthly_salary: {
      below_5000: '💵',
      '5000_9999': '💰',
      '10000_14999': '💸',
      '15000_24999': '🏆',
      '25000_39999': '💎',
      '40000_plus': '👑',
    },
    employment_type: {
      salaried: '👔',
      self_employed: '🔧',
    },
    priorities: {
      no_annual_fees: '🆓',
      high_cashback: '💰',
      travel_benefits: '✈️',
      dining_entertainment: '🍽️',
      low_foreign_fees: '🌍',
      premium_lifestyle: '👑',
    },
  };

  return emojiMap[questionKey]?.[optionKey] || '';
};

/**
 * Get confidence score color class
 * @param {number} confidence - Confidence score (0-1)
 * @returns {string}
 */
export const getConfidenceColor = confidence => {
  if (confidence >= 0.8) return 'text-green-600';
  if (confidence >= 0.6) return 'text-yellow-600';
  return 'text-red-600';
};

/**
 * Get confidence score background color class
 * @param {number} confidence - Confidence score (0-1)
 * @returns {string}
 */
export const getConfidenceBg = confidence => {
  if (confidence >= 0.8) return 'bg-green-500';
  if (confidence >= 0.6) return 'bg-yellow-500';
  return 'bg-red-500';
};

/**
 * Get rank badge color class
 * @param {number} rank - Rank position
 * @returns {string}
 */
export const getRankBadge = rank => {
  switch (rank) {
    case 1:
      return 'bg-yellow-500 text-white';
    case 2:
      return 'bg-gray-400 text-white';
    case 3:
      return 'bg-orange-600 text-white';
    default:
      return 'bg-blue-500 text-white';
  }
};

/**
 * Check if a question is answered
 * @param {any} answer - The answer value
 * @returns {boolean}
 */
export const isQuestionAnswered = answer => {
  if (!answer) return false;
  if (Array.isArray(answer)) return answer.length > 0;
  
  // Handle spending_breakdown (object with categories and amounts)
  if (typeof answer === 'object' && !Array.isArray(answer)) {
    const entries = Object.entries(answer);
    // Must have at least 1 category with a valid amount >= 100 AED
    const validEntries = entries.filter(([category, amount]) => 
      category && category.trim() !== '' && amount >= 100
    );
    return validEntries.length >= 1 && validEntries.length <= 2;
  }
  
  return answer.toString().trim() !== '';
};

/**
 * Format number with commas
 * @param {number} num
 * @returns {string}
 */
export const formatNumber = num => {
  if (typeof num !== 'number') return 'N/A';
  return num.toLocaleString();
};

export function findBestMatch(targetName, availableNames) {
  let bestMatch = null;
  let bestScore = 0;

  for (const name of availableNames) {
    const score = similarity(targetName.toLowerCase(), name.toLowerCase());
    if (score > bestScore && score > 0.6) { // threshold
      bestScore = score;
      bestMatch = name;
    }
  }

  return bestMatch;
}

function similarity(s1, s2) {
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;

  if (longer.length === 0) return 1.0;

  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

function levenshteinDistance(s1, s2) {
  const matrix = Array(s2.length + 1).fill(null).map(() => Array(s1.length + 1).fill(null));

  for (let i = 0; i <= s1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= s2.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= s2.length; j++) {
    for (let i = 1; i <= s1.length; i++) {
      const substitutionCost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + substitutionCost
      );
    }
  }

  return matrix[s2.length][s1.length];
}